import { ILocalStore } from '@kts-front/types';
import { action, computed, makeObservable, observable } from 'mobx';

import { IFormField } from 'stores/interfaces/IFormField';
import { ValueOf } from 'types/valueOf';
import { ErrorType, getError } from 'utils/validators';

type PrivateFields = '_commonErrors';

export abstract class BaseFormStore<FM extends Record<string, IFormField<any>>> implements ILocalStore {
  abstract readonly fieldsMap: FM;
  private _commonErrors: string[] = [];

  constructor() {
    makeObservable<BaseFormStore<FM>, PrivateFields>(this, {
      canSubmit: computed,
      fieldsKeys: computed,
      fieldsErrors: computed,
      _commonErrors: observable.ref,
      errors: computed,
      hasError: computed,
      touched: computed,
      reset: action.bound,
      canReset: computed,
      setCommonErrors: action.bound,
    });
  }

  protected get _fields(): Array<ValueOf<FM>> {
    return Object.values(this.fieldsMap) as Array<ValueOf<FM>>;
  }

  get fieldsKeys(): Array<keyof FM> {
    return Object.keys(this.fieldsMap) as Array<keyof FM>;
  }

  isFieldKey = (key: any): key is keyof FM => {
    return this.fieldsMap[key] !== undefined;
  };

  setFieldSyntheticError = (key: string, message: string): void => {
    if (this.isFieldKey(key)) {
      this.fieldsMap[key].setSyntheticError(getError(message));
    }
  };

  get fieldsErrors(): ErrorType[] {
    return this._fields.reduce<ErrorType[]>((acc, field) => {
      if (field.hasError) {
        acc.push(field.error);
      }

      return acc;
    }, []);
  }

  get errors(): ErrorType[] {
    return [
      ...this._commonErrors.map((error) => ({
        value: true,
        error,
      })),
      ...this.fieldsErrors,
    ];
  }

  get hasError(): boolean {
    return this.errors.length > 0;
  }

  get canSubmit(): boolean {
    return this._fields.every((field) => field.hasError === false);
  }

  get touched(): boolean {
    return this._fields.some((field) => field.touched);
  }

  validateFields = (): boolean => {
    this._fields.forEach((field) => field.validate());

    return this.canSubmit;
  };

  setCommonErrors(errors: string[]): void {
    this._commonErrors = errors;
  }

  disable(): void {
    this._fields.forEach((field) => field.setDisabled(true));
  }

  reset(): void {
    this._fields.forEach((field) => field.reset());
  }

  get canReset(): boolean {
    return this._fields.some((field) => field.hasChanged);
  }

  destroy(): void {
    this._fields.forEach((field) => field.destroy());
  }
}
